/**
 * Onboarding Submit Policy
 *
 * Three independent reactions, each keyed on a loading flag's false→true
 * transition (mirroring the account `billingEntitySubmitPolicy` and the
 * invoicing `invoicingSubmitPolicy`):
 *
 *   - `submittingInvoicing` → provision the selected invoicing connector,
 *     branching via `resolveSetupPanelKind`:
 *       directProvision → `ProvisionSystemIntegration`
 *       apiKey          → POST credentials to the systems-integrations
 *                         `authorize` webhook
 *   - `submittingBilling`   → validate + `AddBillingEntity`
 *   - `completing`          → persist `onboardingCompletedAt` via
 *                             `UpdateAccountInfo` (TV1-1903 D1)
 *
 * No new persistence is introduced — the wizard reuses the canonical backend
 * commands (TV1-1903 D2).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';
import { OnboardingState, selectors } from '../state';
import { OnboardingCommand } from '../commands';
import { ConnectorEntry } from '../../../integrations';
import {
  resolveSetupPanelKind,
  canSubmitApiKey,
  canDirectProvision
} from '../../../integrations/shared/state/capabilityGates';

export const createOnboardingSubmitPolicy = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
): Policy<OnboardingState, OnboardingCommand> => {
  // ---------------------------------------------------------------------------
  // Invoicing
  // ---------------------------------------------------------------------------
  const submitInvoicing = async (
    next: OnboardingState,
    ctx: { dispatch: ( cmd: OnboardingCommand, meta: { source: string }) => void }
  ): Promise<void> => {
    const connectorId = next.invoicing.selectedConnectorId;
    const credentials = next.invoicing.credentials;

    const resetSubmitting = () => {
      ctx.dispatch(
        { type: 'onboarding/invoicing/submit/error', payload: {}},
        { source: 'api' }
      );
    };

    if ( !connectorId ) {
      console.error( '[onboarding] No connector selected for invoicing submission' );
      resetSubmitting();
      return;
    }

    const connector = getConnectors()[connectorId];
    const panelKind = resolveSetupPanelKind( connector );

    try {
      if ( panelKind === 'directProvision' ) {
        if ( !canDirectProvision( connector )) {
          throw new Error(
            `Connector ${connectorId} does not declare the direct-provision capability`
          );
        }

        const integrationUserId = uuidv7();
        const integrationId = `${connectorId}_${integrationUserId}`;
        await backend.commands.ProvisionSystemIntegration({
          id: integrationId,
          body: {
            connectorId,
            integrationUserId
          } as any
        });

        ctx.dispatch(
          { type: 'onboarding/invoicing/submit/success', payload: { integrationId }},
          { source: 'api' }
        );
        return;
      }

      if ( panelKind === 'apiKey' ) {
        if ( !canSubmitApiKey( connector )) {
          throw new Error(
            `Connector ${connectorId} does not declare the api-key capability`
          );
        }

        // Server-side authorization validates + stores credentials and
        // provisions the integration. The provisioned id is the canonical
        // `${connectorId}_<handle>` shape; we surface a deterministic id so the
        // billing step can reference it via `provisionedIntegrationId`.
        await backend.post.systems_integrations_webhooks_post(
          { connectorId, route: 'authorize' },
          { ...credentials }
        );

        const integrationId = `${connectorId}_${uuidv7()}`;
        ctx.dispatch(
          { type: 'onboarding/invoicing/submit/success', payload: { integrationId }},
          { source: 'api' }
        );
        return;
      }

      console.warn(
        `[onboarding] Invoicing submit on connector ${connectorId} with unsupported panel kind '${panelKind}'; ignoring.`
      );
      resetSubmitting();
    } catch ( error ) {
      console.error( '[onboarding] Invoicing submit failed:', error );
      resetSubmitting();
      throw error;
    }
  };

  // ---------------------------------------------------------------------------
  // Billing
  // ---------------------------------------------------------------------------
  const submitBilling = async (
    next: OnboardingState,
    ctx: { dispatch: ( cmd: OnboardingCommand, meta: { source: string }) => void }
  ): Promise<void> => {
    const { form } = next.billing;

    // TV1-2203 — `vatCondition` is the SELLER (emisor) regime and it DECIDES the
    // comprobante class. Left unset it lands on the derivation's
    // `undefined -> responsableInscripto` fallback — the one branch in the whole
    // matrix that GUESSES, on an irreversible fiscal document. Mantine's `required`
    // only renders an asterisk and sets `aria-required`; these forms submit through
    // an `onClick` handler, not a native <form> submit, so nothing was actually
    // blocked. This is the real gate. It is checked HERE rather than folded into
    // the `canSubmit`/valid selector so the user gets a rendered field error instead
    // of a silently disabled button.
    const vatConditionMissing = !form.vatCondition;

    if ( !selectors.selectCanFinishBilling( next ) || vatConditionMissing ) {
      const errors: Record<string, string> = {};
      if ( !form.name.trim()) {
        errors.name = 'Nombre comercial es requerido';
      }
      if ( !form.legalName.trim()) {
        errors.legalName = 'Razón social es requerida';
      }
      if ( !form.documentId.trim()) {
        errors.documentId = 'CUIT es requerido';
      }
      if ( vatConditionMissing ) {
        errors.vatCondition = 'Condición IVA es requerida';
      }
      if ( !form.billingAddress.trim()) {
        errors.billingAddress = 'Dirección fiscal es requerida';
      }
      if ( !form.invoicingIntegrationId.trim()) {
        errors.invoicingIntegrationId = 'La integración de facturación es requerida';
      }

      ctx.dispatch(
        { type: 'onboarding/billing/setErrors', payload: { errors }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'onboarding/billing/submit/error', payload: {}},
        { source: 'api' }
      );
      return;
    }

    try {
      await backend.commands.AddBillingEntity({
        id: uuidv7(),
        body: {
          name: form.name.trim(),
          legalName: form.legalName.trim(),
          documentId: form.documentId.trim(),
          billingAddress: form.billingAddress.trim(),
          vatCondition: form.vatCondition,
          grossIncome: form.grossIncome?.trim(),
          activityStartDate: form.activityStartDate,
          // TV1-2210 (S6) — `BillingEntityFormData.pointsOfSale` IS ARCA's structured
          // record now (shared with the account-domain form), so this is a
          // passthrough. S4's `.map( p => ({ number: Number( p ) }) )` discarded
          // `emisionTipo` / `bloqueado` / `fchBaja`.
          pointsOfSale: form.pointsOfSale || [],
          taxAgency: form.taxAgency as 'arca',
          invoicingIntegrationId:
            next.invoicing.provisionedIntegrationId ?? form.invoicingIntegrationId.trim()
        }
      });

      ctx.dispatch(
        { type: 'onboarding/billing/submit/success', payload: {}},
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[onboarding] Billing submit failed:', error );
      ctx.dispatch(
        { type: 'onboarding/billing/submit/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  // ---------------------------------------------------------------------------
  // Complete
  // ---------------------------------------------------------------------------
  const submitComplete = async (
    next: OnboardingState,
    ctx: { dispatch: ( cmd: OnboardingCommand, meta: { source: string }) => void }
  ): Promise<void> => {
    const accountId = next.data.accountId;

    if ( !accountId ) {
      console.error( '[onboarding] No accountId available to complete onboarding' );
      ctx.dispatch(
        { type: 'onboarding/complete/error', payload: {}},
        { source: 'api' }
      );
      return;
    }

    try {
      await backend.commands.UpdateAccountInfo({
        id: accountId,
        body: {
          onboardingCompletedAt: new Date().toISOString()
        }
      });

      ctx.dispatch(
        { type: 'onboarding/complete/success', payload: {}},
        { source: 'api' }
      );
    } catch ( error ) {
      // Keep the wizard mounted on error so the user can retry.
      console.error( '[onboarding] Complete failed:', error );
      ctx.dispatch(
        { type: 'onboarding/complete/error', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  return {
    name: 'onboarding-submit',

    shouldReact: ( prev, next ) =>
      ( !prev.loading.submittingInvoicing && next.loading.submittingInvoicing ) ||
      ( !prev.loading.submittingBilling && next.loading.submittingBilling ) ||
      ( !prev.loading.completing && next.loading.completing ),

    onStateChange: async ({ prev, next, ctx }) => {
      if ( !prev.loading.submittingInvoicing && next.loading.submittingInvoicing ) {
        await submitInvoicing( next, ctx );
      }
      if ( !prev.loading.submittingBilling && next.loading.submittingBilling ) {
        await submitBilling( next, ctx );
      }
      if ( !prev.loading.completing && next.loading.completing ) {
        await submitComplete( next, ctx );
      }
    }
  };
};
